import React, { useState } from 'react';
import { ExclamationTriangleIcon, CalendarDaysIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../MasterAdmin/ui/ConfirmDialog';
import type { WhatsAppCampaignSendPreflight } from '../../services/MasterAdmin/whatsappCampaignService';
import { formatDateTime } from './whatsappCampaignUi';

export type WeeklyCapChoice = 'exclude' | 'schedule_next_week' | 'override';

type Props = {
  open: boolean;
  loading?: boolean;
  preflight: WhatsAppCampaignSendPreflight | null;
  onCancel: () => void;
  onConfirm: (choice: WeeklyCapChoice) => void;
};

const WhatsAppCampaignSendPreflightDialog: React.FC<Props> = ({
  open,
  loading,
  preflight,
  onCancel,
  onConfirm,
}) => {
  const [choice, setChoice] = useState<WeeklyCapChoice>('exclude');
  const summary = preflight?.summary;
  const weeklyCount = summary?.skipped || 0;
  const nonReaderCount = summary?.excludedNonReaders || 0;
  const ready = summary?.ready || 0;
  const deferred = summary?.deferredWeekly || 0;
  const conflicts = preflight?.weeklyConflicts || [];

  return (
    <ConfirmDialog
      open={open}
      title="Review send policy"
      tone={weeklyCount > 0 || nonReaderCount > 0 ? 'warning' : 'success'}
      confirmLabel={loading ? 'Sending…' : `Send to ${ready} contacts`}
      cancelLabel="Go back"
      busy={loading}
      onCancel={onCancel}
      onConfirm={() => onConfirm(choice)}
      message={
        <div className="space-y-4 text-left">
          {preflight?.sendPlan && (
            <div className="rounded-lg bg-gray-50 p-3 text-xs dark:bg-gray-800">
              <p className="font-semibold text-gray-900 dark:text-white">Send plan</p>
              <p className="mt-1 text-gray-600 dark:text-gray-300">
                {preflight.sendPlan.phaseCount} phases · {preflight.sendPlan.phaseSize} per phase · every{' '}
                {Math.round((preflight.sendPlan.waveDelaySeconds || 0) / 60)} min · ~{' '}
                {preflight.sendPlan.estimatedCompletionHours}h total
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
            <div className="rounded-lg border border-green-200 bg-green-50 p-2 dark:border-green-900 dark:bg-green-950/30">
              <p className="text-green-800 dark:text-green-300">Ready</p>
              <p className="text-lg font-bold text-green-900 dark:text-green-200">{ready}</p>
            </div>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 dark:border-amber-900 dark:bg-amber-950/30">
              <p className="text-amber-800 dark:text-amber-300">Weekly cap</p>
              <p className="text-lg font-bold text-amber-900 dark:text-amber-200">{weeklyCount}</p>
            </div>
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 dark:border-rose-900 dark:bg-rose-950/30">
              <p className="text-rose-800 dark:text-rose-300">Non-readers</p>
              <p className="text-lg font-bold text-rose-900 dark:text-rose-200">{nonReaderCount}</p>
            </div>
            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2 dark:border-indigo-900 dark:bg-indigo-950/30">
              <p className="text-indigo-800 dark:text-indigo-300">Scheduled later</p>
              <p className="text-lg font-bold text-indigo-900 dark:text-indigo-200">{deferred}</p>
            </div>
          </div>

          {weeklyCount > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1 text-xs font-semibold text-amber-800 dark:text-amber-200">
                <ExclamationTriangleIcon className="h-4 w-4" />
                {weeklyCount} contact(s) already received marketing this week
              </p>
              <div className="mb-3 max-h-32 overflow-auto rounded border border-amber-100 dark:border-amber-900/40">
                <table className="min-w-full text-[11px]">
                  <thead className="bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
                    <tr>
                      <th className="px-2 py-1 text-left">Phone</th>
                      <th className="px-2 py-1 text-left">Last sent</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conflicts.slice(0, 20).map((c) => (
                      <tr key={c.phone} className="border-t border-amber-50 dark:border-amber-900/30">
                        <td className="px-2 py-1 font-mono">{c.phone}</td>
                        <td className="px-2 py-1">{formatDateTime(c.sentAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mb-2 text-xs text-gray-600 dark:text-gray-400">Policy: 1 marketing template per user per week.</p>
              <div className="space-y-2 text-xs">
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border p-2 dark:border-gray-700">
                  <input
                    type="radio"
                    name="weeklyCap"
                    checked={choice === 'exclude'}
                    onChange={() => setChoice('exclude')}
                  />
                  <span>
                    <strong>Remove from this send</strong> — skip weekly-capped contacts (recommended)
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border p-2 dark:border-gray-700">
                  <input
                    type="radio"
                    name="weeklyCap"
                    checked={choice === 'schedule_next_week'}
                    onChange={() => setChoice('schedule_next_week')}
                  />
                  <span className="inline-flex items-center gap-1">
                    <CalendarDaysIcon className="h-3.5 w-3.5" />
                    <strong>Schedule for next week</strong> — auto-send {formatDateTime(preflight?.scheduleNextWeekAt)}
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-red-200 p-2 dark:border-red-900/40">
                  <input
                    type="radio"
                    name="weeklyCap"
                    checked={choice === 'override'}
                    onChange={() => setChoice('override')}
                  />
                  <span>
                    <strong>Override policy</strong> — send anyway (logged; use sparingly)
                  </span>
                </label>
              </div>
            </div>
          )}

          {nonReaderCount > 0 && (
            <p className="text-xs text-rose-700 dark:text-rose-300">
              {nonReaderCount} contact(s) excluded — no read on prior marketing in the last{' '}
              {preflight?.policy?.nonReaderExcludeDays || 30} days.
            </p>
          )}
        </div>
      }
    />
  );
};

export default WhatsAppCampaignSendPreflightDialog;
